import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { getTierList, TierListItem } from "./proApi";
import {
  Champ,
  Pct,
  TierChip,
  PatchSelect,
  RoleSelect,
  useProIndex,
} from "./shared";

type Kind = "self" | "syn" | "opp_same" | "opp_diff";

const KINDS: { key: Kind; label: string }[] = [
  { key: "self", label: "Champions" },
  { key: "syn", label: "Synergies" },
  { key: "opp_same", label: "Direct matchups" },
  { key: "opp_diff", label: "Cross-role matchups" },
];

const KIND_HELP: Record<Kind, string> = {
  self: "Every champion-role, ranked by how much it lifts a draft on its own.",
  syn: "Same-team pairs, ranked by how well they perform together.",
  opp_same:
    "Lane matchups (same role): the first champion beats the second one.",
  opp_diff:
    "Cross-role matchups: the first champion beats the second one from another lane.",
};

function ChampLink({
  name,
  role,
  onOpen,
}: {
  name: string;
  role?: string;
  onOpen: (name: string, role?: string) => void;
}) {
  return (
    <button
      type="button"
      className="pro__champ-link"
      title={`Open ${name}'s champion page`}
      onClick={() => onOpen(name, role)}
    >
      <Champ name={name} role={role} />
    </button>
  );
}

function itemLabel(
  it: TierListItem,
  kind: Kind,
  onOpen: (name: string, role?: string) => void,
) {
  if (kind === "self") {
    return <ChampLink name={it.champ || ""} role={it.role} onOpen={onOpen} />;
  }
  const sep = kind === "syn" ? "+" : "vs";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <ChampLink name={it.champ_a || ""} role={it.role_a} onOpen={onOpen} />
      <span className="pro__muted">{sep}</span>
      <ChampLink name={it.champ_b || ""} role={it.role_b} onOpen={onOpen} />
    </span>
  );
}

function TierList() {
  const { patches } = useProIndex();
  const navigate = useNavigate();
  const openChampion = useCallback(
    (name: string, role?: string) => {
      const q = new URLSearchParams({ champion: name });
      if (role) q.set("role", role);
      navigate(`/pro/champions?${q.toString()}`);
    },
    [navigate],
  );
  const [kind, setKind] = useState<Kind>("self");
  const [patch, setPatch] = useState("");
  const [roleA, setRoleA] = useState("");
  const [roleB, setRoleB] = useState("");
  const [weeks, setWeeks] = useState(2);
  const [minRecent, setMinRecent] = useState(5);
  const [minN, setMinN] = useState(5);
  const [sortAsc, setSortAsc] = useState(false);
  const [items, setItems] = useState<TierListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSeq = useRef(0);
  const load = useCallback(() => {
    loadSeq.current += 1;
    const seq = loadSeq.current;
    setLoading(true);
    setError(null);
    setItems([]); // never render old-kind items under the new kind's columns
    getTierList({
      kind,
      patch: patch || undefined,
      role_a: roleA || undefined,
      role_b: roleB || undefined,
      min_recent: minRecent,
      min_n: minN,
      weeks,
    })
      .then((d) => {
        if (seq === loadSeq.current) setItems(d.items);
      })
      .catch((e) => {
        if (seq === loadSeq.current) setError(e.message || "Error");
      })
      .finally(() => {
        if (seq === loadSeq.current) setLoading(false);
      });
  }, [kind, patch, roleA, roleB, minRecent, minN, weeks]);

  useEffect(load, [load]);

  const sorted = useMemo(
    () => (sortAsc ? [...items].reverse() : items),
    [items, sortAsc],
  );

  const minRecentId = useId();
  const minNId = useId();
  const weeksId = useId();
  const usePatch = !!patch;
  const windowLabel = usePatch ? `patch ${patch}` : `last ${weeks} weeks`;
  const nLabel = usePatch ? `N ${patch}` : `N ${weeks} weeks`;

  const itemKey = (it: TierListItem) =>
    kind === "self"
      ? `${it.champ}|${it.role}`
      : `${it.champ_a}|${it.role_a}|${it.champ_b}|${it.role_b}`;

  const rows: React.ReactNode[] = [];
  let lastTier: string | null = null;
  sorted.forEach((it) => {
    if (it.tier !== lastTier) {
      lastTier = it.tier;
      rows.push(
        <tr key={`${kind}|tier-${it.tier}`} className="pro__tier-divider">
          <td colSpan={5}>Tier {it.tier}</td>
        </tr>,
      );
    }
    rows.push(
      <tr key={`${kind}|${itemKey(it)}`}>
        <td>
          <TierChip tier={it.tier} />
        </td>
        <td>{itemLabel(it, kind, openChampion)}</td>
        <td>
          <Pct value={it.pct} />
        </td>
        <td className="pro__muted">
          {usePatch ? it.n_patch : it.n_window ?? it.n_recent}
        </td>
        <td>
          <Pct value={it.note} />
        </td>
      </tr>,
    );
  });

  return (
    <div className="pro__page">
      <div className="pro__page-head">
        <h1>Tier list</h1>
      </div>
      <p className="pro__page-sub">{KIND_HELP[kind]}</p>

      <div className="pro__toolbar">
        <div className="pro__segmented">
          {KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              className={kind === k.key ? "is-active" : ""}
              onClick={() => setKind(k.key)}
            >
              {k.label}
            </button>
          ))}
        </div>
        <PatchSelect patches={patches} value={patch} onChange={setPatch} />
        <RoleSelect
          label={kind === "self" ? "Role" : "Role A"}
          value={roleA}
          onChange={setRoleA}
        />
        {kind !== "self" && (
          <RoleSelect label="Role B" value={roleB} onChange={setRoleB} />
        )}
        {!usePatch && (
          <label className="pro__field" htmlFor={weeksId}>
            Recent window (weeks)
            <input
              id={weeksId}
              type="number"
              min={1}
              value={weeks}
              onChange={(e) =>
                setWeeks(Math.max(1, parseInt(e.target.value || "1", 10)))
              }
            />
          </label>
        )}
        <label className="pro__field" htmlFor={minRecentId}>
          Min games {windowLabel} ≥
          <input
            id={minRecentId}
            type="number"
            min={0}
            value={minRecent}
            onChange={(e) =>
              setMinRecent(Math.max(0, parseInt(e.target.value || "0", 10)))
            }
          />
        </label>
        <label className="pro__field" htmlFor={minNId}>
          Min games total ≥
          <input
            id={minNId}
            type="number"
            min={0}
            value={minN}
            onChange={(e) =>
              setMinN(Math.max(0, parseInt(e.target.value || "0", 10)))
            }
          />
        </label>
      </div>

      <div className="pro__card">
        <div className="pro__card-title">
          {KINDS.find((k) => k.key === kind)?.label}
          <span className="pro__card-note">
            {loading ? "Loading…" : `${items.length} entries`}
          </span>
        </div>
        <div className="pro__scroll">
          {error && <div className="pro__empty">{error}</div>}
          {!error && !loading && !items.length && (
            <div className="pro__empty">
              No entry matches the filters. Lower the minimum game counts.
            </div>
          )}
          {!error && items.length > 0 && (
            <table className="pro__table">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Interaction</th>
                  <th
                    className="is-sortable"
                    onClick={() => setSortAsc((v) => !v)}
                    data-tip="Percentile of this interaction's strength within its own type. Click to flip the order."
                  >
                    Percentile {sortAsc ? "▲" : "▼"}
                  </th>
                  <th
                    data-tip={`Games featuring this interaction over the ${windowLabel} (competitive tiers 1-3).`}
                  >
                    {nLabel}
                  </th>
                  <th data-tip="Recency score 0-100, from the number of games featuring this interaction over the last 2 weeks (fixed window). Scaled per interaction type: one of the most-played interactions of its type scores ~95, an unplayed one scores 0.">
                    Recency
                  </th>
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default TierList;
